/**
 * Members import service — MOCK (CT-5634). Contract TBD (ARP-1480 / backend); platform
 * envelope, snake_case bodies. Nothing here runs before the last wizard step (nobody is
 * added or invited until the user confirms), except the template download.
 */
import { envelope } from '../shared/utils/mockServiceHelpers.js';
import { buildImportTemplate } from '../shared/import/buildImportTemplate.js';
import { listGroups } from './groupsService.js';
import { createMembers, listMembers, setMemberStatus, updateMember } from './membersService.js';
import { listRoles } from './rolesService.js';
import { listStudios } from './studiosService.js';
import { WORKSPACE } from './seeds/members.js';
import { nextId } from './seeds/store.js';

export const IMPORT_TEMPLATE_FILE_NAME = 'members-template.csv';

/**
 * Template file listing the workspace roles and groups, generated client-side.
 * TODO(CT-5634 / backend): `GET /people-access/members/import/template` — contract TBD (ARP-1480).
 */
export const getImportTemplate = async () => {
  const [roles, groups] = await Promise.all([listRoles(), listGroups()]);
  const content = buildImportTemplate({
    roles: roles.body.items.map((role) => role.name),
    groups: groups.body.items.map((group) => group.name),
  });
  return envelope({ file_name: IMPORT_TEMPLATE_FILE_NAME, content });
};

/**
 * Add the reviewed people (and invite them, when asked) as one import batch.
 * TODO(CT-5634 / backend): `POST /people-access/members/import` — contract TBD (ARP-1480).
 */
export const commitImport = (payload = {}) =>
  createMembers({ ...payload, import_batch_id: payload.import_batch_id ?? nextId('imp') });

const CATALOG_PAGE_SIZE = 1000;

/**
 * What the review step checks the file against: roles, groups, existing members and the
 * workspace domain. Read once when leaving the match step; nothing is written.
 * Mock of `GET /people-access/members/import/catalog` (contract TBD with the backend, ARP-1480).
 * Members past the first `CATALOG_PAGE_SIZE` are not read here, so a real contract has to match
 * by email on the server instead of loading the whole list.
 */
export const getImportCatalog = async () => {
  const [roles, groups, members, studios] = await Promise.all([
    listRoles(),
    listGroups(),
    listMembers({ page_size: CATALOG_PAGE_SIZE }),
    listStudios(),
  ]);
  const studioNames = Object.fromEntries(studios.body.items.map(({ id, name }) => [id, name]));
  return envelope({
    domain: WORKSPACE.domain,
    // A role's studios travel by name, as the review step shows them.
    roles: roles.body.items.map(({ id, name, studios: ids }) => ({
      id,
      name,
      studios: (ids ?? []).map((studioId) => studioNames[studioId] ?? studioId),
    })),
    groups: groups.body.items.map(({ id, name }) => ({ id, name })),
    members: members.body.items.map(
      ({ id, email, name, user_type, status, job_title, location }) => ({
        id,
        email,
        name,
        user_type,
        status,
        job_title: job_title ?? '',
        location: location ?? '',
      }),
    ),
  });
};

const PROFILE_FIELDS = ['first_name', 'last_name', 'job_title'];

/**
 * Add the reviewed people as one batch: new people (invited or not), profile updates of
 * existing members (never their access), and a re-invitation of existing members who have not
 * joined. Returns `{ batch_id, added, updated, invited, failed[] }`.
 * TODO(CT-5637 / backend): `POST /people-access/members/import` — contract TBD (ARP-1480):
 * endpoint, batch id, partial failures and idempotency.
 *
 * @param {{
 *   file_name: string, import_date: string, note?: string,
 *   new_members: Array<{ row_number: number, email: string, first_name: string, last_name: string, job_title: string, user_type: string, role_ids: string[], group_ids: string[], invite: boolean }>,
 *   updates: Array<{ member_id: string, first_name?: string, last_name?: string, job_title?: string }>,
 *   reinvite_member_ids: string[],
 * }} payload
 */
export const submitImport = async (payload) => {
  const batchId = nextId('imp');
  const failed = [];
  let added = 0;
  let invited = 0;
  let firstError = null;

  for (const invite of [true, false]) {
    const group = payload.new_members.filter((row) => row.invite === invite);
    if (group.length === 0) continue;
    const res = await createMembers({
      members: group,
      send_invitation: invite,
      note: payload.note,
      import_batch: { id: batchId, file_name: payload.file_name },
    });
    if (res.status_code !== 200) {
      // The other group may already exist: report this one as failed instead of dropping the result.
      firstError ??= res;
      group.forEach((row) => failed.push({ row_number: row.row_number, reason: 'create_failed' }));
      continue;
    }
    added += res.body.created_count;
    if (invite) invited += res.body.created_count;
    res.body.rejected.forEach((rejection) => failed.push(rejection));
  }

  let updated = 0;
  for (const { member_id, ...fields } of payload.updates) {
    const patch = Object.fromEntries(PROFILE_FIELDS.filter((key) => fields[key]).map((key) => [key, fields[key]]));
    // Nothing in the file differs from the member: there is nothing to send or to count.
    if (Object.keys(patch).length === 0) continue;
    const res = await updateMember(member_id, patch);
    if (res.status_code === 200) updated += 1;
    else failed.push({ member_id, reason: 'update_failed' });
  }

  for (const id of payload.reinvite_member_ids) {
    const res = await setMemberStatus(id, 'resend');
    if (res.status_code === 200) invited += 1;
    else failed.push({ member_id: id, reason: 'invite_failed' });
  }

  // Nothing went through and a call failed: the whole import failed, the wizard can try again.
  if (firstError && added + updated + invited === 0) return firstError;
  return envelope({ batch_id: batchId, added, updated, invited, failed });
};
